// RITA-1547 — Abfragen des Entwurfs „Vertrag erstellen“ (Backend: getContractCreationDraft,
// saveContractCreationDraft, discardContractCreationDraft, nur Super-Admins). Die Karte fragt ohne
// Dokumente ab; erst die Bearbeiten-Seite lädt beide Dokumente.
// RITA-1552: Der Entwurf „Rücksendung drucken“ ist ein eigener Entwurf (Server: `process: "return"`, nur das Anschreiben
// Rücksendung). Er hat eigene, benannte Dokumente mit festem `process` statt einer Variablen am Vertragsentwurf, weil
// die Tests der Karte nach Dokument (Identität) und die der Bearbeiten-Seite nach Operationsname weichen. Die Dokumente
// des Vertrags bleiben unverändert (ohne `process` = Vertrag). ENTWURFSDOKUMENTE ordnet sie je Vorgang zu.
import { gql } from '@apollo/client';
import type { DocumentNode } from 'graphql';
import type {
  ContractCreationDraft,
  ContractCreationDraftDocument,
  ContractCreationDraftInput,
  ContractCreationProcess,
} from 'global-intern-types';

export type Vertragsentwurf = ContractCreationDraft;
export type Entwurfsdokument = ContractCreationDraftDocument;

export interface VertragsentwurfAntwort {
  getContractCreationDraft: Vertragsentwurf | null;
}

export interface VertragsentwurfVariablen {
  agent_id: number;
}

export interface EntwurfSpeichernAntwort {
  saveContractCreationDraft: Vertragsentwurf;
}

export interface EntwurfSpeichernVariablen {
  agent_id: number;
  input: ContractCreationDraftInput;
  /**
   * Revision, die ersetzt werden darf (geöffnet, in der Rückfrage bestätigt oder aus der Warnung); null = keine
   * bestätigte Revision, etwa nach „inzwischen verworfen“. Gibt es dann doch einen Entwurf, meldet der Server CONFLICT.
   */
  expected_revision: string | null;
}

export interface EntwurfVerwerfenVariablen {
  agent_id: number;
  expected_revision: string;
}

const ENTWURF_FELDER = `
  agent_id
  revision
  created_at
  updated_at
  updated_by_name
  advisor_values
`;

const DOKUMENT_FELDER = `
  kind
  template_id
  template_name
  template_updated_at
  template_status
  template_current_updated_at
`;

export const GET_VERTRAGSENTWURF = gql`
  query getVertragsentwurf($agent_id: Int!) {
    getContractCreationDraft(agent_id: $agent_id) {
      ${ENTWURF_FELDER}
      documents {
        ${DOKUMENT_FELDER}
      }
    }
  }
`;

export const GET_VERTRAGSENTWURF_MIT_DOKUMENTEN = gql`
  query getVertragsentwurfMitDokumenten($agent_id: Int!) {
    getContractCreationDraft(agent_id: $agent_id) {
      ${ENTWURF_FELDER}
      documents {
        ${DOKUMENT_FELDER}
        document
      }
    }
  }
`;

export const SAVE_VERTRAGSENTWURF = gql`
  mutation saveVertragsentwurf($agent_id: Int!, $input: ContractCreationDraftInput!, $expected_revision: String) {
    saveContractCreationDraft(agent_id: $agent_id, input: $input, expected_revision: $expected_revision) {
      ${ENTWURF_FELDER}
      documents {
        ${DOKUMENT_FELDER}
      }
    }
  }
`;

export const DISCARD_VERTRAGSENTWURF = gql`
  mutation discardVertragsentwurf($agent_id: Int!, $expected_revision: String!) {
    discardContractCreationDraft(agent_id: $agent_id, expected_revision: $expected_revision)
  }
`;

// RITA-1552 – Entwurf der Rücksendung (eigene Revision; Antworten, Variablen und Typen wie beim Vertrag)

export const GET_RUECKSENDUNGSENTWURF = gql`
  query getRuecksendungsentwurf($agent_id: Int!) {
    getContractCreationDraft(agent_id: $agent_id, process: "return") {
      ${ENTWURF_FELDER}
      documents {
        ${DOKUMENT_FELDER}
      }
    }
  }
`;

export const GET_RUECKSENDUNGSENTWURF_MIT_DOKUMENTEN = gql`
  query getRuecksendungsentwurfMitDokumenten($agent_id: Int!) {
    getContractCreationDraft(agent_id: $agent_id, process: "return") {
      ${ENTWURF_FELDER}
      documents {
        ${DOKUMENT_FELDER}
        document
      }
    }
  }
`;

export const SAVE_RUECKSENDUNGSENTWURF = gql`
  mutation saveRuecksendungsentwurf(
    $agent_id: Int!
    $input: ContractCreationDraftInput!
    $expected_revision: String
  ) {
    saveContractCreationDraft(
      agent_id: $agent_id
      input: $input
      expected_revision: $expected_revision
      process: "return"
    ) {
      ${ENTWURF_FELDER}
      documents {
        ${DOKUMENT_FELDER}
      }
    }
  }
`;

export const DISCARD_RUECKSENDUNGSENTWURF = gql`
  mutation discardRuecksendungsentwurf($agent_id: Int!, $expected_revision: String!) {
    discardContractCreationDraft(agent_id: $agent_id, expected_revision: $expected_revision, process: "return")
  }
`;

/** Die vier Entwurfs-Dokumente eines Vorgangs (Karte ohne, Seite mit `document`; Speichern; Verwerfen). */
export interface Entwurfsdokumente {
  abfrage: DocumentNode;
  abfrageMitDokumenten: DocumentNode;
  speichern: DocumentNode;
  verwerfen: DocumentNode;
}

export const ENTWURFSDOKUMENTE: Readonly<Record<ContractCreationProcess, Entwurfsdokumente>> = {
  contract: {
    abfrage: GET_VERTRAGSENTWURF,
    abfrageMitDokumenten: GET_VERTRAGSENTWURF_MIT_DOKUMENTEN,
    speichern: SAVE_VERTRAGSENTWURF,
    verwerfen: DISCARD_VERTRAGSENTWURF,
  },
  return: {
    abfrage: GET_RUECKSENDUNGSENTWURF,
    abfrageMitDokumenten: GET_RUECKSENDUNGSENTWURF_MIT_DOKUMENTEN,
    speichern: SAVE_RUECKSENDUNGSENTWURF,
    verwerfen: DISCARD_RUECKSENDUNGSENTWURF,
  },
};
